// apps/client/src/ui/hud/Icons.tsx — 24-unit line icons for the HUD (stroke currentColor, 2.4 wide; `.round-btn svg` styles them)
const svg = { viewBox: '0 0 24 24', 'aria-hidden': true as const, focusable: 'false' as const };
const line = { fill: 'none', stroke: 'currentColor', strokeWidth: 2.4, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

export function Bag() {
  return <svg {...svg}><path {...line} d="M5 8.5h14l-1.2 11a1.5 1.5 0 0 1-1.5 1.3H7.7a1.5 1.5 0 0 1-1.5-1.3z" /><path {...line} d="M9 8.5V7a3 3 0 0 1 6 0v1.5" /></svg>;
}
export function Envelope() {
  return <svg {...svg}><rect {...line} x="3.5" y="6" width="17" height="12.5" rx="2.2" /><path {...line} d="m4.5 7.5 7.5 6 7.5-6" /></svg>;
}
/** Sign-in's "towns are invite-only" note (title polish, 2026-10-06): a house with its door. */
export function House() {
  return <svg {...svg}><path {...line} d="M4 11.2 12 4.5l8 6.7" /><path {...line} d="M6.2 9.6v9.4a1.3 1.3 0 0 0 1.3 1.3h9a1.3 1.3 0 0 0 1.3-1.3V9.6" /><path {...line} d="M10 20.3v-5.1h4v5.1" /></svg>;
}
/** The title screen's music switch (2026-10-05): a pair of quavers, struck through when the music is off. */
export function Music(p: { off?: boolean }) {
  return (
    <svg {...svg}>
      <path {...line} d="M9 17.5V6.5l10-2v11" /><circle {...line} cx="6.8" cy="17.5" r="2.3" /><circle {...line} cx="16.8" cy="15.5" r="2.3" />
      {p.off && <path {...line} d="M4 4l16 16" />}
    </svg>
  );
}
export function Menu() {
  return <svg {...svg}><path {...line} d="M5 7h14M5 12h14M5 17h14" /></svg>;
}
export function Chat() {
  return <svg {...svg}><path {...line} d="M5.5 5h13a2 2 0 0 1 2 2v7.5a2 2 0 0 1-2 2H11l-4.5 3.5v-3.5h-1a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z" /></svg>;
}
export function Smile() {
  return <svg {...svg}><circle {...line} cx="12" cy="12" r="8.5" /><path {...line} d="M8.6 14a4.2 4.2 0 0 0 6.8 0M9.3 9.6h.01M14.7 9.6h.01" /></svg>;
}
/** B (hold to run): a runner, as in the brand HUD. */
export function Run() {
  return (
    <svg {...svg}>
      <circle {...line} cx="14.5" cy="4.6" r="1.7" />
      <path {...line} d="M7.5 10.2 10.8 8l3.6 1.1 1.8 3.1 2.8.9M10.8 8l-1.6 5.4 3.4 2.6-.9 4.4M9.2 13.4 7.6 17 4.5 17.6" />
    </svg>
  );
}
/** Tool (from M1): a fishing hook, as in the brand HUD. */
export function Hook() {
  return <svg {...svg}><path {...line} d="M14 3.5v11a4 4 0 0 1-8 0v-2.2l2.4 2" /><circle {...line} cx="14" cy="3.5" r="1" /></svg>;
}
/** The clock pill's glyph: a sun-400 disc with a wood ring and rays. */
export function Sun() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="1.15em" height="1.15em">
      <circle cx="12" cy="12" r="4.6" fill="var(--sun-400)" stroke="var(--wood-700)" strokeWidth="2" />
      <path d="M12 2.8v2.1M12 19.1v2.1M2.8 12h2.1M19.1 12h2.1M5.5 5.5 7 7M17 17l1.5 1.5M5.5 18.5 7 17M17 7l1.5-1.5"
        fill="none" stroke="var(--wood-700)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
/** The clock pill's glyph at night: a crescent in the same wood ring. */
export function Moon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="1.15em" height="1.15em">
      <path d="M15.8 4.2a8 8 0 1 0 4 12.4 6.6 6.6 0 0 1-4-12.4z" fill="var(--cream-200)" stroke="var(--wood-700)" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}
export function Coin() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="1.15em" height="1.15em">
      <circle cx="12" cy="12" r="8.4" fill="var(--sun-400)" stroke="var(--wood-700)" strokeWidth="2" />
      <path d="M12 7.6v8.8M9.6 10a2.4 1.9 0 0 1 4.8 0M14.4 14a2.4 1.9 0 0 1-4.8 0" fill="none" stroke="var(--wood-700)" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
/** Full screen (2026-10-15): the Share button as browsers draw it (a tray with an arrow up), for "tap Share, then Add to Home Screen". */
export function Share() {
  return <svg {...svg}><path {...line} d="M8.5 9.5H7a1.8 1.8 0 0 0-1.8 1.8v7.4A1.8 1.8 0 0 0 7 20.5h10a1.8 1.8 0 0 0 1.8-1.8v-7.4A1.8 1.8 0 0 0 17 9.5h-1.5" /><path {...line} d="M12 14.5v-11M8.6 6.6 12 3.3l3.4 3.3" /></svg>;
}
/** …and installing: an arrow down onto a phone lying sideways (the game plays in landscape). */
export function Install() {
  return <svg {...svg}><rect {...line} x="2.8" y="12.2" width="18.4" height="9" rx="2" /><path {...line} d="M12 2.8v8M8.8 7.8 12 11l3.2-3.2" /></svg>;
}
export function Close() {
  return <svg {...svg}><path {...line} d="M7 7l10 10M17 7 7 17" /></svg>;
}
/** The Full screen button (2026-10-18): four corners pushed out to the screen's edges, or (exit) pulled in. */
export function Expand(p: { exit?: boolean }) {
  return (
    <svg {...svg}>
      <path {...line} d={p.exit ? 'M9 4.5V9H4.5M15 4.5V9h4.5M9 19.5V15H4.5M15 19.5V15h4.5' : 'M4.5 9V4.5H9M19.5 9V4.5H15M4.5 15v4.5H9M19.5 15v4.5H15'} />
    </svg>
  );
}
